import React, { cloneElement, createElement, Children } from "react";
import PropTypes from "prop-types";

import { Root, FlexChild } from "./styled-components";
import { generateResponsiveProps } from "../helpers";

import {
  BASE_PROPS,
  JUSTIFY_CHOICES,
  ALIGN_CHOICES,
  WRAP_CHOICES,
} from "./constants";

const Flex = ({
  as: asComponent,
  className,
  direction,
  reverse,
  justify,
  alignItems,
  children,
  fullHeight,
  wrap,
  gutter,
  dataQa,
  style = {},
  ...rest
}) => {
  const responsiveProps = generateResponsiveProps(rest, BASE_PROPS);

  return (
    <Root
      alignItems={alignItems}
      as={asComponent}
      className={className}
      data-qa={dataQa}
      direction={direction}
      fullHeight={fullHeight}
      gutter={gutter}
      justify={justify}
      reverse={reverse}
      wrap={wrap}
      style={style}
      {...responsiveProps}
    >
      {Children.map(children, (child) => {
        if (!child) return child;

        const { alignself, basis, grow, order, shrink, wide } = child
          ? child.props
          : {};

        return createElement(
          FlexChild,
          {
            grow,
            order,
            basis,
            /**
             * * alignSelf is not being parsed as valid HTML attribute,
             * * so we are using lowercase instead.
             */
            alignself,
            shrink,
            wide,
          },
          // * We are overriding props to undefined avoiding their propagation
          cloneElement(child, {
            alignself: undefined,
            basis: undefined,
            grow: undefined,
            order: undefined,
            shrink: undefined,
            wide: undefined,
          }),
        );
      })}
    </Root>
  );
};

const responsiveProps = {
  gutter: PropTypes.number,
  direction: PropTypes.oneOf(["column", "row"]),
  justify: PropTypes.oneOf(JUSTIFY_CHOICES),
  alignItems: PropTypes.oneOf(ALIGN_CHOICES),
  reverse: PropTypes.bool,
};

Flex.propTypes = {
  /** align-items flexbox property */
  alignItems: PropTypes.oneOf(ALIGN_CHOICES),
  /** Extends Flex with the received one */
  as: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
  /** Components to be rendered inside Flex */
  children: PropTypes.node,
  /** Add a css class to the Flex */
  className: PropTypes.string,
  dataQa: PropTypes.string,
  /** flex-direction flexbox property */
  direction: PropTypes.oneOf(["column", "row"]),
  /** When true sets the height to 100% otherwise auto. */
  fullHeight: PropTypes.bool,
  /** Sets a multiplier of 8px margin between children */
  gutter: PropTypes.number,
  /** justify-content flexbox property */
  justify: PropTypes.oneOf(JUSTIFY_CHOICES),
  /** Sets the style configuration for the following media query: (min-width:992px) */
  lg: PropTypes.shape(responsiveProps),
  /** Sets the style configuration for the following media query: (min-width:768px) */
  md: PropTypes.shape(responsiveProps),
  /** Sets the reverse direction depending on the direction selected (row-reverse/column-reverse) */
  reverse: PropTypes.bool,
  /** Sets the style configuration for the following media query: (min-width:576px) */
  sm: PropTypes.shape(responsiveProps),
  /**  flex-wrap flexbox property */
  wrap: PropTypes.oneOf(WRAP_CHOICES),
  /** Sets the style configuration for the following media query: (min-width:1200px) */
  xl: PropTypes.shape(responsiveProps),
  /** Sets the style configuration for the following media query: (min-width:0px) */
  xs: PropTypes.shape(responsiveProps),
  /** Sets the style configuration for the following media query: (min-width:1440px) */
  xxl: PropTypes.shape(responsiveProps),
};

Flex.defaultProps = {
  alignItems: "stretch",
  as: "div",
  children: null,
  className: "",
  direction: "row",
  fullHeight: false,
  gutter: 0,
  justify: "flexStart",
  lg: undefined,
  md: undefined,
  reverse: false,
  sm: undefined,
  wrap: "nowrap",
  xl: undefined,
  xs: undefined,
  xxl: undefined,
};

export default Flex;
